import React from 'react';
import clsx from 'clsx';

/**
 * Badge — small status pill.
 *
 * Variants: default, brand, secondary, success, warning, danger, info, neutral, solid
 * Sizes:    sm, md, lg
 * Tone:     soft (default — tinted bg), solid (filled), outline
 */
const TONES = {
    soft: {
        default:   'bg-surface-muted text-ink-muted border border-line',
        brand:     'bg-brand-50 text-brand-700 border border-brand-200',
        secondary: 'bg-secondary-50 text-secondary-700 border border-secondary-200',
        success:   'bg-success-50 text-success-700 border border-success-100',
        warning:   'bg-warning-50 text-warning-700 border border-warning-100',
        danger:    'bg-danger-50 text-danger-700 border border-danger-100',
        info:      'bg-info-50 text-info-700 border border-info-100',
        neutral:   'bg-surface-muted text-ink-muted border border-line',
    },
    solid: {
        default:   'bg-ink text-ink-inverse border border-ink',
        brand:     'bg-brand-500 text-ink-inverse border border-brand-600',
        secondary: 'bg-secondary-500 text-ink-inverse border border-secondary-600',
        success:   'bg-success-500 text-ink-inverse border border-success-600',
        warning:   'bg-warning-500 text-ink-inverse border border-warning-600',
        danger:    'bg-danger-500 text-ink-inverse border border-danger-600',
        info:      'bg-info-500 text-ink-inverse border border-info-600',
        neutral:   'bg-ink text-ink-inverse border border-ink',
    },
    outline: {
        default:   'bg-transparent text-ink-muted border border-line',
        brand:     'bg-transparent text-brand-700 border border-brand-300',
        secondary: 'bg-transparent text-secondary-700 border border-secondary-300',
        success:   'bg-transparent text-success-700 border border-success-500/40',
        warning:   'bg-transparent text-warning-700 border border-warning-500/40',
        danger:    'bg-transparent text-danger-700 border border-danger-500/40',
        info:      'bg-transparent text-info-700 border border-info-500/40',
        neutral:   'bg-transparent text-ink-muted border border-line',
    },
};

const SIZES = {
    sm: 'text-2xs px-2 py-0.5  gap-1',
    md: 'text-xs  px-2.5 py-1  gap-1.5',
    lg: 'text-sm  px-3 py-1.5  gap-1.5',
};

const ICON_SIZES = { sm: 'w-3 h-3', md: 'w-3.5 h-3.5', lg: 'w-4 h-4' };

export function Badge({
    children,
    variant = 'default',
    tone = 'soft',
    size = 'md',
    className,
    icon,
    dot = false,
    removable = false,
    onRemove,
}) {
    const palette = (TONES[tone] && (TONES[tone][variant] || TONES[tone].default)) || TONES.soft.default;

    return (
        <span className={clsx(
            'inline-flex items-center font-medium rounded-pill leading-none',
            palette,
            SIZES[size] || SIZES.md,
            className,
        )}>
            {dot && (
                <span aria-hidden="true" className={clsx(
                    'inline-block w-1.5 h-1.5 rounded-pill',
                    tone === 'solid' ? 'bg-white/80' : 'bg-current opacity-70',
                )} />
            )}
            {icon && (
                <span aria-hidden="true" className={clsx('inline-flex items-center shrink-0', ICON_SIZES[size])}>
                    {icon}
                </span>
            )}
            <span>{children}</span>
            {removable && (
                <button
                    type="button"
                    onClick={onRemove}
                    className="-ms-0.5 -me-1 ms-1 rounded-pill p-0.5 hover:bg-black/10 transition-colors"
                    aria-label="הסר"
                >
                    <svg className={ICON_SIZES[size]} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            )}
        </span>
    );
}

export default Badge;
